/**
 * API Types
 * Request/Response types for API calls
 */

import {
  Job,
  JobCreate,
  JobUpdate,
  JobListItem,
  Customer,
  CustomerCreate,
  CustomerUpdate,
  Vehicle,
  VehicleCreate,
  VehicleUpdate,
  Driver,
  DriverCreate,
  DriverUpdate,
  AccidentDetail,
  AccidentDetailCreate,
  AccidentDetailUpdate,
} from './job';

import {
  Document,
  DocumentPage,
  DocumentUploadRequest,
  DocumentUploadResponse,
  DocumentClassifyRequest,
  DocumentClassifyResponse,
  DocumentExtractRequest,
  DocumentExtractResponse,
  FieldVerifyRequest,
  BulkFieldVerifyRequest,
  DocumentMapRequest,
  DocumentMapResponse,
  DocumentListItem,
} from './document';

import {
  PaginatedResponse,
  ApiResponse,
  FilterOptions,
  SortOptions,
  ActivityLog,
} from './common';

// ============================================================================
// JOB ENDPOINTS
// ============================================================================

/**
 * GET /api/jobs - List jobs
 */
export interface JobsListRequest {
  page?: number;
  page_size?: number;
  filters?: FilterOptions;
  sort?: SortOptions;
}

export type JobsListResponse = ApiResponse<PaginatedResponse<JobListItem>>;

/**
 * GET /api/jobs/:id - Get job by ID
 */
export interface JobGetRequest {
  id: string;
  include_relations?: boolean; // Include customer, vehicle, driver, accident_detail
}

export type JobGetResponse = ApiResponse<Job>;

/**
 * POST /api/jobs - Create job
 */
export interface JobCreateRequest {
  data: JobCreate;
}

export type JobCreateResponse = ApiResponse<Job>;

/**
 * PATCH /api/jobs/:id - Update job
 */
export interface JobUpdateRequest {
  id: string;
  data: JobUpdate;
}

export type JobUpdateResponse = ApiResponse<Job>;

/**
 * DELETE /api/jobs/:id - Delete job
 */
export interface JobDeleteRequest {
  id: string;
}

export type JobDeleteResponse = ApiResponse<{ success: boolean }>;

/**
 * GET /api/jobs/:id/activity - Get job activity log
 */
export interface JobActivityRequest {
  id: string;
  page?: number;
  page_size?: number;
}

export type JobActivityResponse = ApiResponse<PaginatedResponse<ActivityLog>>;

// ============================================================================
// CUSTOMER ENDPOINTS
// ============================================================================

/**
 * GET /api/customers/:id - Get customer by ID
 */
export interface CustomerGetRequest {
  id: string;
}

export type CustomerGetResponse = ApiResponse<Customer>;

/**
 * POST /api/customers - Create customer
 */
export interface CustomerCreateRequest {
  data: CustomerCreate;
}

export type CustomerCreateResponse = ApiResponse<Customer>;

/**
 * PATCH /api/customers/:id - Update customer
 */
export interface CustomerUpdateRequest {
  id: string;
  data: CustomerUpdate;
}

export type CustomerUpdateResponse = ApiResponse<Customer>;

/**
 * DELETE /api/customers/:id - Delete customer
 */
export interface CustomerDeleteRequest {
  id: string;
}

export type CustomerDeleteResponse = ApiResponse<{ success: boolean }>;

/**
 * GET /api/customers/search - Search customers
 */
export interface CustomerSearchRequest {
  query: string;
  page?: number;
  page_size?: number;
}

export type CustomerSearchResponse = ApiResponse<PaginatedResponse<Customer>>;

// ============================================================================
// VEHICLE ENDPOINTS
// ============================================================================

/**
 * GET /api/vehicles/:id - Get vehicle by ID
 */
export interface VehicleGetRequest {
  id: string;
}

export type VehicleGetResponse = ApiResponse<Vehicle>;

/**
 * POST /api/vehicles - Create vehicle
 */
export interface VehicleCreateRequest {
  data: VehicleCreate;
}

export type VehicleCreateResponse = ApiResponse<Vehicle>;

/**
 * PATCH /api/vehicles/:id - Update vehicle
 */
export interface VehicleUpdateRequest {
  id: string;
  data: VehicleUpdate;
}

export type VehicleUpdateResponse = ApiResponse<Vehicle>;

/**
 * DELETE /api/vehicles/:id - Delete vehicle
 */
export interface VehicleDeleteRequest {
  id: string;
}

export type VehicleDeleteResponse = ApiResponse<{ success: boolean }>;

/**
 * GET /api/vehicles/search - Search vehicles by registration
 */
export interface VehicleSearchRequest {
  registration_number: string;
}

export type VehicleSearchResponse = ApiResponse<Vehicle[]>;

// ============================================================================
// DRIVER ENDPOINTS
// ============================================================================

/**
 * GET /api/drivers/:id - Get driver by ID
 */
export interface DriverGetRequest {
  id: string;
}

export type DriverGetResponse = ApiResponse<Driver>;

/**
 * POST /api/drivers - Create driver
 */
export interface DriverCreateRequest {
  data: DriverCreate;
}

export type DriverCreateResponse = ApiResponse<Driver>;

/**
 * PATCH /api/drivers/:id - Update driver
 */
export interface DriverUpdateRequest {
  id: string;
  data: DriverUpdate;
}

export type DriverUpdateResponse = ApiResponse<Driver>;

/**
 * DELETE /api/drivers/:id - Delete driver
 */
export interface DriverDeleteRequest {
  id: string;
}

export type DriverDeleteResponse = ApiResponse<{ success: boolean }>;

/**
 * GET /api/drivers/search - Search drivers by license
 */
export interface DriverSearchRequest {
  license_no: string;
}

export type DriverSearchResponse = ApiResponse<Driver[]>;

// ============================================================================
// ACCIDENT DETAIL ENDPOINTS
// ============================================================================

/**
 * GET /api/accident-details/:id - Get accident detail by ID
 */
export interface AccidentDetailGetRequest {
  id: string;
}

export type AccidentDetailGetResponse = ApiResponse<AccidentDetail>;

/**
 * POST /api/accident-details - Create accident detail
 */
export interface AccidentDetailCreateRequest {
  data: AccidentDetailCreate;
}

export type AccidentDetailCreateResponse = ApiResponse<AccidentDetail>;

/**
 * PATCH /api/accident-details/:id - Update accident detail
 */
export interface AccidentDetailUpdateRequest {
  id: string;
  data: AccidentDetailUpdate;
}

export type AccidentDetailUpdateResponse = ApiResponse<AccidentDetail>;

/**
 * DELETE /api/accident-details/:id - Delete accident detail
 */
export interface AccidentDetailDeleteRequest {
  id: string;
}

export type AccidentDetailDeleteResponse = ApiResponse<{ success: boolean }>;

// ============================================================================
// DOCUMENT ENDPOINTS
// ============================================================================

/**
 * GET /api/jobs/:job_id/documents - List documents for job
 */
export interface DocumentsListRequest {
  job_id: string;
  page?: number;
  page_size?: number;
  document_type?: string;
  document_category?: string;
  status?: string;
}

export type DocumentsListResponse = ApiResponse<PaginatedResponse<DocumentListItem>>;

/**
 * GET /api/documents/:id - Get document by ID
 */
export interface DocumentGetRequest {
  id: string;
  include_pages?: boolean;
}

export type DocumentGetResponse = ApiResponse<Document>;

/**
 * POST /api/documents/upload - Upload document
 */
export type DocumentUploadRequestPayload = DocumentUploadRequest;
export type DocumentUploadResponsePayload = ApiResponse<DocumentUploadResponse>;

/**
 * POST /api/documents/:id/classify - Classify document
 */
export type DocumentClassifyRequestPayload = DocumentClassifyRequest;
export type DocumentClassifyResponsePayload = ApiResponse<DocumentClassifyResponse>;

/**
 * POST /api/documents/:id/extract - Extract data from document
 */
export type DocumentExtractRequestPayload = DocumentExtractRequest;
export type DocumentExtractResponsePayload = ApiResponse<DocumentExtractResponse>;

/**
 * DELETE /api/documents/:id - Delete document
 */
export interface DocumentDeleteRequest {
  id: string;
}

export type DocumentDeleteResponse = ApiResponse<{ success: boolean }>;

// ============================================================================
// DOCUMENT PAGE ENDPOINTS
// ============================================================================

/**
 * GET /api/documents/:document_id/pages/:page_number - Get document page
 */
export interface DocumentPageGetRequest {
  document_id: string;
  page_number: number;
}

export type DocumentPageGetResponse = ApiResponse<DocumentPage>;

/**
 * PATCH /api/document-pages/:id/verify - Verify extracted field
 */
export type FieldVerifyRequestPayload = FieldVerifyRequest;
export type FieldVerifyResponsePayload = ApiResponse<DocumentPage>;

/**
 * PATCH /api/document-pages/:id/verify-bulk - Bulk verify fields
 */
export type BulkFieldVerifyRequestPayload = BulkFieldVerifyRequest;
export type BulkFieldVerifyResponsePayload = ApiResponse<DocumentPage>;

// ============================================================================
// DOCUMENT MAPPING ENDPOINTS
// ============================================================================

/**
 * POST /api/documents/:id/map - Map document to entity
 */
export type DocumentMapRequestPayload = DocumentMapRequest;
export type DocumentMapResponsePayload = ApiResponse<DocumentMapResponse>;

/**
 * GET /api/documents/:id/mappings - Get document mappings
 */
export interface DocumentMappingsGetRequest {
  document_id: string;
}

export type DocumentMappingsGetResponse = ApiResponse<{
  mappings: Array<{
    entity_type: string;
    entity_id: string;
    field_count: number;
    mapped_at: string;
  }>;
}>;

// ============================================================================
// UTILITY ENDPOINTS
// ============================================================================

/**
 * GET /api/jobs/:id/completion - Get completion status
 */
export interface JobCompletionRequest {
  id: string;
}

export type JobCompletionResponse = ApiResponse<{
  overall: number;
  sections: {
    customer: number;
    vehicle: number;
    driver: number;
    accident: number;
    documents: number;
  };
}>;

/**
 * GET /api/jobs/:id/validation - Validate job data
 */
export interface JobValidationRequest {
  id: string;
}

export type JobValidationResponse = ApiResponse<{
  valid: boolean;
  errors: Array<{
    section: string;
    field: string;
    message: string;
  }>;
  warnings: Array<{
    section: string;
    field: string;
    message: string;
  }>;
}>;

// ============================================================================
// WEBSOCKET EVENTS (for real-time updates)
// ============================================================================

/**
 * WebSocket event types
 */
export enum WebSocketEventType {
  DOCUMENT_UPLOADED = 'document:uploaded',
  DOCUMENT_CLASSIFIED = 'document:classified',
  DOCUMENT_EXTRACTED = 'document:extracted',
  DOCUMENT_VERIFIED = 'document:verified',
  JOB_UPDATED = 'job:updated',
  JOB_ASSIGNED = 'job:assigned',
  JOB_COMPLETED = 'job:completed',
}

/**
 * WebSocket event payload
 */
export interface WebSocketEvent<T = unknown> {
  type: WebSocketEventType;
  job_id: string;
  data: T;
  timestamp: string;
  user_id?: string;
}

// ============================================================================
// AXIOS CONFIG TYPES
// ============================================================================

/**
 * API client configuration
 */
export interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
  withCredentials?: boolean;
}

/**
 * API request config (extends Axios config)
 */
export interface ApiRequestConfig {
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  signal?: AbortSignal;
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void;
}

// ============================================================================
// REACT QUERY TYPES
// ============================================================================

/**
 * Query keys for React Query
 */
export const QueryKeys = {
  // Jobs
  jobs: ['jobs'] as const,
  jobsList: (filters?: FilterOptions) => ['jobs', 'list', filters] as const,
  job: (id: string) => ['jobs', id] as const,
  jobActivity: (id: string) => ['jobs', id, 'activity'] as const,
  jobCompletion: (id: string) => ['jobs', id, 'completion'] as const,
  jobValidation: (id: string) => ['jobs', id, 'validation'] as const,

  // Customers
  customers: ['customers'] as const,
  customer: (id: string) => ['customers', id] as const,
  customerSearch: (query: string) => ['customers', 'search', query] as const,

  // Vehicles
  vehicles: ['vehicles'] as const,
  vehicle: (id: string) => ['vehicles', id] as const,
  vehicleSearch: (registration: string) => ['vehicles', 'search', registration] as const,

  // Drivers
  drivers: ['drivers'] as const,
  driver: (id: string) => ['drivers', id] as const,
  driverSearch: (license: string) => ['drivers', 'search', license] as const,

  // Accident Details
  accidentDetails: ['accident-details'] as const,
  accidentDetail: (id: string) => ['accident-details', id] as const,

  // Documents
  documents: ['documents'] as const,
  jobDocuments: (jobId: string) => ['documents', 'job', jobId] as const,
  document: (id: string) => ['documents', id] as const,
  documentPage: (documentId: string, pageNumber: number) =>
    ['documents', documentId, 'pages', pageNumber] as const,
  documentMappings: (documentId: string) => ['documents', documentId, 'mappings'] as const,
} as const;

/**
 * Mutation keys for React Query
 */
export const MutationKeys = {
  // Jobs
  createJob: ['createJob'] as const,
  updateJob: ['updateJob'] as const,
  deleteJob: ['deleteJob'] as const,

  // Customers
  createCustomer: ['createCustomer'] as const,
  updateCustomer: ['updateCustomer'] as const,
  deleteCustomer: ['deleteCustomer'] as const,

  // Vehicles
  createVehicle: ['createVehicle'] as const,
  updateVehicle: ['updateVehicle'] as const,
  deleteVehicle: ['deleteVehicle'] as const,

  // Drivers
  createDriver: ['createDriver'] as const,
  updateDriver: ['updateDriver'] as const,
  deleteDriver: ['deleteDriver'] as const,

  // Accident Details
  createAccidentDetail: ['createAccidentDetail'] as const,
  updateAccidentDetail: ['updateAccidentDetail'] as const,
  deleteAccidentDetail: ['deleteAccidentDetail'] as const,

  // Documents
  uploadDocument: ['uploadDocument'] as const,
  deleteDocument: ['deleteDocument'] as const,
  classifyDocument: ['classifyDocument'] as const,
  extractDocument: ['extractDocument'] as const,
  verifyField: ['verifyField'] as const,
  verifyFieldsBulk: ['verifyFieldsBulk'] as const,
  mapDocument: ['mapDocument'] as const,
} as const;
